import axios from 'axios';
import type { CrmApplication, ApplicationsListResponse } from '../types/crm';
import { api, authAxios, API_BASE } from './apiClient';

// Re-export shared instances
export { authAxios } from './apiClient';

// Re-export constants for backward compatibility
export {
  STATUS_LABELS,
  SERVICE_TYPE_LABELS,
  SERVICE_TYPE_COLORS,
  STATUS_COLORS,
  LOAN_STATUS_LABELS,
  LOAN_STATUS_COLORS,
} from './constants';

export const crmApi = {
  // Initialize CRM schema
  init: async () => {
    const response = await api.post('/init');
    return response.data;
  },

  // Draft autosave (public, no auth)
  saveDraft: async (data: Record<string, unknown>) => {
    const response = await axios.post('/api/crm/applications/draft', data);
    return response.data;
  },

  getDraft: async (token: string) => {
    const response = await axios.get(`/api/client-portal/${token}/application-data`);
    return response.data;
  },

  // Applications
  submitApplication: async (data: Record<string, unknown>) => {
    const response = await api.post('/applications', data);
    return response.data;
  },

  getApplications: async (filters?: {
    status?: string;
    search?: string;
    serviceType?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: string;
    all?: boolean;
    assignedTo?: string; // 'me' | 'unassigned' | operator_id
    edition_year?: number;
    is_archived?: string; // 'true' | 'false' | 'all'
  }) => {
    const response = await api.get('/applications', { params: filters });
    return response.data as ApplicationsListResponse;
  },

  getApplication: async (id: number) => {
    const response = await api.get(`/applications/${id}`);
    return response.data as CrmApplication;
  },

  updateStatus: async (id: number, status: string) => {
    const response = await api.patch(`/applications/${id}/status`, { status });
    return response.data;
  },

  updateApplication: async (id: number, data: Partial<CrmApplication>) => {
    const response = await api.patch(`/applications/${id}`, data);
    return response.data;
  },

  deleteApplication: async (id: number) => {
    const response = await api.delete(`/applications/${id}`);
    return response.data;
  },

  assignOperator: async (appId: number, operatorId: number | null) => {
    const response = await api.post(`/applications/${appId}/assign`, { operator_id: operatorId });
    return response.data;
  },

  recalculatePoints: async (id: number) => {
    const response = await api.post(`/applications/${id}/recalculate-points`);
    return response.data;
  },

  generateAIAnalysis: async (id: number) => {
    const response = await api.post(`/applications/${id}/ai-analysis`);
    return response.data;
  },

  generateProductAnalysis: async (id: number) => {
    const response = await authAxios.post(`/api/product-analysis/${id}`);
    return response.data;
  },

  runProductAIAction: async (applicationId: number, actionType: string, additionalData?: Record<string, any>) => {
    const response = await authAxios.post('/api/product-ai-action', { applicationId, actionType, additionalData });
    return response.data;
  },

  generateOptimizationReport: async (id: number, category?: string) => {
    const response = await api.post(`/applications/${id}/optimize`, { category: category || 'all' });
    return response.data;
  },

  generatePurchaseList: async (id: number) => {
    const response = await api.post(`/applications/${id}/generate-purchase-list`);
    return response.data;
  },

  generateFinancialForecast: async (id: number) => {
    const response = await api.post(`/applications/${id}/financial-forecast`);
    return response.data;
  },

  generateTaxAdvice: async (id: number) => {
    const response = await api.post(`/applications/${id}/tax-advisor`);
    return response.data;
  },

  generateMarketResearch: async (id: number) => {
    const response = await api.post(`/applications/${id}/market-research`);
    return response.data;
  },

  generateCertificates: async (id: number, type?: string, formData?: Record<string, unknown>) => {
    const response = await api.post(`/applications/${id}/generate-certificates`, {
      type: type || 'all',
      formData,
    });
    return response.data;
  },

  generateCertificatePdf: async (pdfData: {
    title: string;
    content: string;
    companyName?: string;
    companyAddress?: string;
    contactPerson?: string;
    contactPosition?: string;
    issueDate?: string;
  }) => {
    const response = await api.post('/certificates/generate-pdf', pdfData, {
      responseType: 'blob',
    });
    // Trigger download
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${pdfData.title.replace(/[^a-zA-Z0-9 ]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  generateBusinessProfileAI: async (id: number) => {
    const response = await api.post(`/applications/${id}/generate-business-profile-ai`);
    return response.data;
  },

  getCertificateAISuggestions: async (id: number, certificateType: string, position: string, companyName: string) => {
    const response = await api.post(`/applications/${id}/certificate-ai-suggestions`, {
      certificateType,
      position,
      companyName,
    });
    return response.data;
  },

  // Course System
  getCourses: async () => {
    const response = await api.get('/courses');
    return response.data;
  },

  getCourseCategories: async () => {
    const response = await api.get('/courses/categories');
    return response.data;
  },

  searchCourses: async (query: string) => {
    const response = await api.get('/courses/search', { params: { q: query } });
    return response.data;
  },

  recommendCourses: async (id: number) => {
    const response = await api.post(`/applications/${id}/recommend-courses`);
    return response.data;
  },

  generateCourseCertificate: async (id: number, courseId: string, startDate?: string, endDate?: string, grade?: string) => {
    const response = await api.post(`/applications/${id}/generate-course-certificate`, {
      courseId,
      startDate,
      endDate,
      grade,
    });
    return response.data;
  },

  generateCourseCertificatesBulk: async (id: number, courseIds: string[]) => {
    const response = await api.post(`/applications/${id}/generate-course-certificates-bulk`, {
      courseIds,
    });
    return response.data;
  },

  getAvailableYears: async (serviceType?: string) => {
    const params: Record<string, string> = {};
    if (serviceType) params.serviceType = serviceType;
    const response = await api.get('/available-years', { params });
    return response.data as { years: { year: number; total: number; active: number; archived: number }[] };
  },

  getArchiveStats: async (serviceType?: string) => {
    const params: Record<string, string> = {};
    if (serviceType) params.serviceType = serviceType;
    const response = await api.get('/archive-stats', { params });
    return response.data as {
      crm: { service_type: string; year: number; total: number; archived: number; active: number; success: number; rejected: number; cancelled: number; has_email: number; has_phone: number; has_notes: number; with_comments: number; with_documents: number }[];
      training: { year: number; sessions: number; archived_sessions: number; enrollments: number; certificates: number; unique_participants: number }[];
      turnusy: { year: number; trips: number; archived_trips: number; bookings: number; unique_clients: number }[];
      wycieczki: { year: number; trips: number; archived_trips: number; bookings: number }[];
    };
  },

  getStats: async (serviceType?: string, editionYear?: number, isArchived?: string) => {
    const params: Record<string, string | number> = {};
    if (serviceType) params.serviceType = serviceType;
    if (editionYear) params.edition_year = editionYear;
    if (isArchived !== undefined) params.is_archived = isArchived;
    const response = await api.get('/stats', { params });
    return response.data;
  },

  // Archive toggle
  toggleArchive: async (id: number, isArchived: boolean) => {
    const response = await api.patch(`/applications/${id}/archive`, { is_archived: isArchived });
    return response.data;
  },

  // Generated Applications
  generateApplication: async (id: number) => {
    const response = await api.post(`/generate-application/${id}`);
    return response.data;
  },

  getGeneratedApplication: async (applicationId: number) => {
    const response = await api.get(`/generated-application/${applicationId}`);
    return response.data;
  },

  updateGeneratedApplication: async (id: number, data: { operator_version?: string }) => {
    const response = await api.put(`/generated-application/${id}`, data);
    return response.data;
  },

  // Documents
  getContract: async (id: number) => {
    const response = await api.get(`/applications/${id}/contract`, {
      responseType: 'blob'
    });
    return response.data;
  },

  getDocuments: async (id: number) => {
    const response = await api.get(`/applications/${id}/documents`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Points Calculation
  calculatePoints: async (id: number, data: { business_type?: string; is_location_in_krakow?: boolean }) => {
    const response = await api.post(`/applications/${id}/calculate-points`, data);
    return response.data;
  },

  // AI Chat
  aiChat: async (applicationId: number, message: string, history: Array<{ role: string; content: string }>) => {
    const response = await api.post(`/applications/${applicationId}/ai-chat`, {
      message,
      history
    });
    return response.data.response;
  },

  aiAssistant: async (message: string, history: Array<{ role: string; content: string }>, context?: string) => {
    const response = await authAxios.post('/api/ai/assistant', { message, history, context });
    return {
      response: response.data.response as string,
      sources: (response.data.sources || []) as string[],
    };
  },

  kfsAdvisor: async (message: string, history: Array<{ role: string; content: string }>, companyId?: number) => {
    const response = await authAxios.post('/api/ai/kfs-advisor', { message, history, companyId });
    return response.data.response as string;
  },

  // Portal Messages (staff ↔ client chat)
  getMessages: async (applicationId: number) => {
    const response = await api.get(`/applications/${applicationId}/messages`);
    return response.data;
  },
  sendMessage: async (applicationId: number, message: string, internal = false) => {
    const response = await api.post(`/applications/${applicationId}/messages`, { message, internal });
    return response.data;
  },
  markMessagesRead: async (applicationId: number) => {
    const response = await api.post(`/applications/${applicationId}/messages/mark-read`);
    return response.data;
  },

  // Email Templates
  getEmailTemplates: async () => {
    const response = await api.get('/email-templates');
    return response.data.templates;
  },

  previewEmail: async (applicationId: number, templateId: string) => {
    const response = await api.post(`/applications/${applicationId}/preview-email/${templateId}`);
    return response.data;
  },

  sendTemplateEmail: async (applicationId: number, data: { subject: string; html: string; templateId?: string }) => {
    const response = await api.post(`/applications/${applicationId}/send-template-email`, data);
    return response.data;
  },

  // Document Templates
  getDocumentTemplates: async () => {
    const response = await api.get('/document-templates');
    return response.data.templates;
  },

  downloadDocumentTemplate: async (templateName: string) => {
    const response = await api.get(`/document-templates/${encodeURIComponent(templateName)}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // PUP Application Generator
  generatePUPApplication: async (id: number) => {
    const response = await api.post(`/applications/${id}/generate-pup-application`, {}, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Document Generation with Placeholders
  getBusinessTypes: async () => {
    const response = await api.get('/business-types');
    return response.data;
  },

  getRecommendedTemplates: async (businessTypeId: number) => {
    const response = await api.get(`/templates/recommended/${businessTypeId}`);
    return response.data;
  },

  getAllDocumentTemplates: async () => {
    const response = await api.get('/templates');
    return response.data;
  },

  suggestTemplatesWithAI: async (businessIdea: string) => {
    const response = await api.get('/templates/suggest', {
      params: { businessIdea }
    });
    return response.data;
  },

  generateDocument: async (applicationId: number, templateId: number, additionalData?: Record<string, unknown>) => {
    const response = await api.post(
      `/applications/${applicationId}/generate-document`,
      { templateId, additionalData },
      { responseType: 'blob' }
    );
    return response.data;
  },

  getGeneratedDocuments: async (applicationId: number) => {
    const response = await api.get(`/applications/${applicationId}/generated-documents`);
    return response.data;
  },

  // Excel Export
  exportToExcel: async (filters?: {
    status?: string;
    fromDate?: string;
    toDate?: string;
    minPoints?: number;
    serviceType?: string;
  }) => {
    const response = await api.get('/applications/export/excel', {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  },

  // PDF Export
  generatePUPApplicationPDF: async (applicationId: number) => {
    const response = await api.post(
      `/applications/${applicationId}/generate-pup-application-pdf`,
      {},
      { responseType: 'blob' }
    );
    return response.data;
  },

  generateDocumentPDF: async (applicationId: number, templateId: number, additionalData?: Record<string, unknown>) => {
    const response = await api.post(
      `/applications/${applicationId}/generate-document-pdf`,
      { templateId, additionalData },
      { responseType: 'blob' }
    );
    return response.data;
  },

  // Email Sending
  sendDocumentPackage: async (applicationId: number, templateIds: number[]) => {
    const response = await api.post(`/applications/${applicationId}/send-documents`, {
      templateIds
    });
    return response.data;
  },

  sendWelcomeEmail: async (applicationId: number) => {
    const response = await api.post(`/applications/${applicationId}/send-welcome`);
    return response.data;
  },

  getIpdPreview: async (applicationId: number) => {
    const response = await api.get(`/applications/${applicationId}/ipd-preview`);
    return response.data;
  },

  sendIpdEmail: async (applicationId: number) => {
    const response = await api.post(`/applications/${applicationId}/send-ipd`);
    return response.data;
  },

  sendNotificationEmail: async (applicationId: number, subject: string, message: string) => {
    const response = await api.post(`/applications/${applicationId}/send-notification`, {
      subject,
      message
    });
    return response.data;
  },

  // PDF Export
  getCertificatesList: async (applicationId: number) => {
    const response = await api.get(`/applications/${applicationId}/certificates`);
    return response.data;
  },

  downloadCertificatePDF: (certificateId: number) => {
    // Open PDF in new tab
    window.open(`/api/crm/certificates/${certificateId}/pdf`, '_blank');
  },

  downloadAllCertificatesPDF: (applicationId: number) => {
    // Open PDF bundle in new tab
    window.open(`/api/crm/applications/${applicationId}/certificates/pdf`, '_blank');
  },

  // Template Editor
  getTemplates: async (type?: string) => {
    const params = type ? { type } : {};
    const response = await api.get('/editable-templates', { params });
    return response.data;
  },

  getTemplate: async (id: number) => {
    const response = await api.get(`/editable-templates/${id}`);
    return response.data;
  },

  createTemplate: async (templateData: Record<string, unknown>) => {
    const response = await api.post('/editable-templates', templateData);
    return response.data;
  },

  updateTemplate: async (id: number, templateData: Record<string, unknown>) => {
    const response = await api.put(`/editable-templates/${id}`, templateData);
    return response.data;
  },

  deleteTemplate: async (id: number) => {
    const response = await api.delete(`/editable-templates/${id}`);
    return response.data;
  },

  // Dashboard (uses separate base URLs but same auth interceptor)
  getDashboardStats: async (serviceType?: string) => {
    const params = serviceType ? { serviceType } : {};
    const response = await authAxios.get('/api/dashboard/stats', { params });
    return response.data;
  },

  getDashboardChart: async (serviceType?: string) => {
    const params = serviceType ? { serviceType } : {};
    const response = await authAxios.get('/api/dashboard/chart', { params });
    return response.data;
  },

  getDashboardTimeline: async (applicationId: number) => {
    const response = await authAxios.get(`/api/dashboard/timeline/${applicationId}`);
    return response.data;
  },

  // Meetings
  getMeetings: async (params?: { from?: string; to?: string; applicationId?: number; serviceType?: string }) => {
    const response = await authAxios.get('/api/meetings', { params });
    return response.data;
  },

  createMeeting: async (data: {
    applicationId?: number;
    title: string;
    description?: string;
    meetingDate: string;
    durationMinutes?: number;
    location?: string;
  }) => {
    const response = await authAxios.post('/api/meetings', data);
    return response.data;
  },

  updateMeeting: async (id: number, data: Partial<{ applicationId: number; title: string; description: string; meetingDate: string; durationMinutes: number; location: string }>) => {
    const response = await authAxios.patch(`/api/meetings/${id}`, data);
    return response.data;
  },

  deleteMeeting: async (id: number) => {
    const response = await authAxios.delete(`/api/meetings/${id}`);
    return response.data;
  },

  // Admin
  getSettings: async () => {
    const response = await authAxios.get('/api/admin/settings');
    return response.data;
  },

  getAuditLogs: async (params?: { action?: string; entityType?: string; limit?: number; offset?: number }) => {
    const response = await authAxios.get('/api/admin/audit-logs', { params });
    return response.data;
  },

  getNotifications: async (serviceType?: string) => {
    const params = serviceType ? { serviceType } : {};
    const response = await authAxios.get('/api/admin/notifications', { params });
    return response.data;
  },

  // In-app team notifications
  getTeamNotifications: async (unread?: boolean) => {
    const params = unread ? { unread: 'true' } : {};
    const response = await authAxios.get('/api/admin/team-notifications', { params });
    return response.data;
  },
  markNotificationsRead: async (ids?: number[]) => {
    const response = await authAxios.post('/api/admin/team-notifications/read', { ids });
    return response.data;
  },
  // Notification log
  getNotificationLog: async (params?: { limit?: number; offset?: number; channel?: string }) => {
    const response = await authAxios.get('/api/admin/notification-log', { params });
    return response.data;
  },
  // WhatsApp broadcast
  broadcastWhatsApp: async (message: string, targetAll?: boolean, targetIds?: number[]) => {
    const response = await authAxios.post('/api/admin/broadcast-whatsapp', { message, targetAll, targetIds });
    return response.data;
  },
  sendInAppNotification: async (data: { userIds?: number[]; title: string; message: string; type?: string; actionUrl?: string }) => {
    const response = await authAxios.post('/api/admin/send-notification', data);
    return response.data;
  },
  // WhatsApp status
  getWhatsAppStatus: async () => {
    const response = await authAxios.get('/api/admin/whatsapp-status');
    return response.data;
  },
  getWhatsAppQR: async () => {
    const response = await authAxios.get('/api/admin/whatsapp-qr');
    return response.data;
  },
  getWhatsAppRecipients: async () => {
    const response = await authAxios.get('/api/admin/whatsapp-recipients');
    return response.data;
  },

  // Bulk SMS
  sendBulkSms: async (recipients: Array<{ phone: string; message: string; applicationId?: number }>) => {
    const response = await authAxios.post('/api/sms/bulk', { recipients });
    return response.data;
  },

  sendSms: async (phone: string, message: string, applicationId?: number) => {
    const response = await authAxios.post('/api/sms/send', { phone, message, applicationId });
    return response.data;
  },

  getSmsBalance: async () => {
    const response = await authAxios.get('/api/sms/balance');
    return response.data;
  },

  // Industry Intelligence
  resolveIndustry: async (data: { pkdCode?: string; description?: string; firstName?: string; lastName?: string; entityType?: string; applicationId?: number }) => {
    const response = await api.post('/resolve-industry', data);
    return response.data;
  },

  getIndustries: async () => {
    const response = await api.get('/industries');
    return response.data;
  },

  grammarPreview: async (data: { firstName: string; lastName: string; entityType?: string }) => {
    const response = await api.post('/grammar-preview', data);
    return response.data;
  },

  getSmartDocTypes: async () => {
    const response = await api.get('/smart-doc-types');
    return response.data as Array<{ id: string; label: string; category: string; description: string }>;
  },

  generateSmartDocument: async (
    applicationId: number,
    templateType: string,
    additionalData?: Record<string, string>,
    format: 'pdf' | 'docx' = 'pdf',
  ) => {
    const response = await api.post(
      `/applications/${applicationId}/generate-smart-document`,
      { templateType, additionalData, format },
      { responseType: 'blob' },
    );
    return response;
  },

  getProductDocTypes: async (productCode: string) => {
    const response = await api.get(`/product-doc-types/${productCode}`);
    return response.data as Array<{ id: string; label: string; product: string; category: string; description: string }>;
  },

  generateProductDocument: async (
    applicationId: number,
    docType: string,
    additionalData?: Record<string, string>,
    format: 'pdf' | 'docx' = 'pdf',
  ) => {
    const response = await api.post(
      `/applications/${applicationId}/generate-product-document`,
      { docType, additionalData, format },
      { responseType: 'blob' },
    );
    return response;
  },

  generateAIDocument: async (applicationId: number, docType: string, additionalContext?: string) => {
    const response = await api.post(`/applications/${applicationId}/generate-ai-document`, { docType, additionalContext });
    return response.data as { title: string; productCode: string; content: string; metadata: any };
  },

  // Document Studio — HTML preview + export
  generateDocumentHTML: async (applicationId: number, docType: string, additionalData?: Record<string, string>) => {
    const response = await api.post(`/applications/${applicationId}/generate-document-html`, { docType, additionalData });
    return response.data as { html: string; metadata: { title: string; fileName: string; applicant: string } };
  },

  htmlToPdf: async (html: string, fileName: string) => {
    const response = await api.post('/documents/html-to-pdf', { html, fileName }, { responseType: 'blob' });
    return response;
  },

  htmlToDocx: async (html: string, fileName: string) => {
    const response = await api.post('/documents/html-to-docx', { html, fileName }, { responseType: 'blob' });
    return response;
  },

  generateSmartDocumentBundle: async (applicationId: number) => {
    const response = await api.post(
      `/applications/${applicationId}/generate-smart-document-bundle`,
      {},
      { responseType: 'blob' },
    );
    return response;
  },

  // Contract Acceptance
  createContractAcceptance: async (applicationId: number, signingMethod?: 'direct' | 'sms' | 'autenti') => {
    const response = await authAxios.post('/api/contract-acceptance/create', {
      applicationId,
      signing_method: signingMethod || 'direct',
    });
    return response.data;
  },

  getContractAcceptanceStatus: async (applicationId: number) => {
    const response = await authAxios.get(`/api/contract-acceptance/status/${applicationId}`);
    return response.data;
  },

  // Check Autenti signing status (public endpoint, no auth needed)
  checkAutentiStatus: async (token: string) => {
    const response = await axios.get(`/api/contract-acceptance/${token}/autenti-status`);
    return response.data;
  },

  // Grammar preview (with optional applicationId for overrides)
  getGrammarPreview: async (firstName: string, lastName: string, applicationId?: number) => {
    const response = await authAxios.get(`/api/grammar/preview`, {
      params: { firstName, lastName, ...(applicationId ? { applicationId } : {}) },
    });
    return response.data;
  },

  // Grammar override - save manual corrections
  saveGrammarOverride: async (applicationId: number, overrides: Record<string, string>) => {
    const response = await authAxios.patch(`/api/grammar/override/${applicationId}`, { overrides });
    return response.data;
  },

  // Grammar override - clear all
  clearGrammarOverride: async (applicationId: number) => {
    const response = await authAxios.delete(`/api/grammar/override/${applicationId}`);
    return response.data;
  },

  // Industry override - save manual industry correction
  saveIndustryOverride: async (applicationId: number, override: {
    id: string; name: string; category: string; clauses?: string[]; templatePriority?: Record<string, number>;
  }) => {
    const response = await api.patch(`/applications/${applicationId}/industry-override`, { override });
    return response.data;
  },

  // Industry override - clear (back to auto-detection)
  clearIndustryOverride: async (applicationId: number) => {
    const response = await api.delete(`/applications/${applicationId}/industry-override`);
    return response.data;
  },

  // Guarantors CRUD (Wsparcie na Starcie)
  getGuarantors: async (applicationId: number) => {
    const response = await authAxios.get(`/api/guarantors/${applicationId}`);
    return response.data;
  },
  createGuarantor: async (applicationId: number, data: Record<string, unknown>) => {
    const response = await authAxios.post(`/api/guarantors/${applicationId}`, data);
    return response.data;
  },
  updateGuarantor: async (id: number, data: Record<string, unknown>) => {
    const response = await authAxios.patch(`/api/guarantors/item/${id}`, data);
    return response.data;
  },
  deleteGuarantor: async (id: number) => {
    const response = await authAxios.delete(`/api/guarantors/item/${id}`);
    return response.data;
  },

  // Webhooks
  getWebhooks: async () => {
    const response = await authAxios.get('/api/admin/webhooks');
    return response.data;
  },

  createWebhook: async (data: { name: string; event_type: string; webhook_url: string; secret_key?: string; headers?: Record<string, string>; is_enabled?: boolean }) => {
    const response = await authAxios.post('/api/admin/webhooks', data);
    return response.data;
  },

  updateWebhook: async (id: number, data: Partial<{ name: string; event_type: string; webhook_url: string; secret_key: string; headers: Record<string, string>; is_enabled: boolean }>) => {
    const response = await authAxios.put(`/api/admin/webhooks/${id}`, data);
    return response.data;
  },

  deleteWebhook: async (id: number) => {
    const response = await authAxios.delete(`/api/admin/webhooks/${id}`);
    return response.data;
  },

  testWebhook: async (id: number) => {
    const response = await authAxios.post(`/api/admin/webhooks/${id}/test`);
    return response.data;
  },

  getWebhookLogs: async (id: number, limit?: number) => {
    const response = await authAxios.get(`/api/admin/webhooks/${id}/logs`, { params: { limit } });
    return response.data;
  },

  generateWebhookKey: async () => {
    const response = await authAxios.post('/api/admin/webhooks/generate-key');
    return response.data;
  },

  // Diagnostics
  getDiagnostics: async () => {
    const response = await authAxios.get('/api/admin/diagnostics');
    return response.data;
  },

  runDiagnosticsAction: async (action: string, target?: string) => {
    const response = await authAxios.post('/api/admin/diagnostics/action', { action, target });
    return response.data;
  },

  // Loan operators (Wsparcie na Starcie)
  getLoanOperators: async () => {
    const response = await api.get('/loan-operators');
    return response.data;
  },

  getLoanOperatorsPublic: async () => {
    const response = await axios.get(`${API_BASE}/loan-operators-public`);
    return response.data;
  },

  updateLoanStatus: async (id: number, loanStatus: string) => {
    const response = await api.patch(`/applications/${id}/loan-status`, { loanStatus });
    return response.data;
  },

  // Universal product status update (works for any status field)
  updateProductStatus: async (id: number, statusField: string, newStatus: string, productCode: string) => {
    const response = await api.patch(`/applications/${id}/product-status`, { statusField, newStatus, productCode });
    return response.data;
  },

  // Bulk status change for multiple applications
  bulkStatusChange: async (ids: number[], statusField: string, newStatus: string, productCode: string) => {
    const response = await api.post('/applications/bulk-status', { ids, statusField, newStatus, productCode });
    return response.data;
  },

  // Bulk email to multiple applications
  bulkEmail: async (applicationIds: number[], subject: string, html: string, templateId?: string) => {
    const response = await api.post('/bulk-email', { applicationIds, subject, html, templateId });
    return response.data;
  },
  // Bulk SMS to multiple applications
  bulkSms: async (applicationIds: number[], message: string) => {
    const response = await api.post('/bulk-sms', { applicationIds, message });
    return response.data;
  },
  // Saved views CRUD
  getSavedViews: async (productCode: string) => {
    const response = await api.get('/saved-views', { params: { product: productCode } });
    return response.data;
  },
  saveView: async (name: string, filters: Record<string, string>, productCode: string, isShared?: boolean) => {
    const response = await api.post('/saved-views', { name, filters, product_code: productCode, is_shared: isShared });
    return response.data;
  },
  deleteView: async (id: number) => {
    const response = await api.delete(`/saved-views/${id}`);
    return response.data;
  },
  scanDuplicates: async () => {
    const response = await api.get('/applications/duplicates/scan');
    return response.data;
  },
  mergeDuplicates: async (primaryId: number, mergeIds: number[]) => {
    const response = await api.post('/applications/duplicates/merge', { primaryId, mergeIds });
    return response.data;
  },
  scheduleEmail: async (data: { applicationId?: number; recipientEmail: string; recipientName?: string; subject: string; bodyHtml: string; scheduledAt: string }) => {
    const response = await api.post('/scheduled-emails', data);
    return response.data;
  },
  quickSearch: async (q: string) => {
    const response = await api.get(`/quick-search?q=${encodeURIComponent(q)}`);
    return response.data;
  },
  getScheduledEmails: async (status = 'pending') => {
    const response = await api.get(`/scheduled-emails?status=${status}`);
    return response.data;
  },
  cancelScheduledEmail: async (id: number) => {
    const response = await api.delete(`/scheduled-emails/${id}`);
    return response.data;
  },

  // Quick add application from Kanban (operator-facing, minimal fields)
  quickAddApplication: async (data: {
    firstName: string;
    lastName: string;
    phone?: string;
    email?: string;
    notes?: string;
    serviceType: string;
  }) => {
    const response = await api.post('/applications/quick-add', data);
    return response.data;
  },

  // Loan-specific AI endpoints
  generateLoanBusinessPlan: async (id: number) => {
    const response = await api.post(`/applications/${id}/generate-loan-business-plan`, {}, { responseType: 'blob' });
    return response.data;
  },

  generateLoanCostEstimate: async (id: number) => {
    const response = await api.post(`/applications/${id}/generate-loan-cost-estimate`, {}, { responseType: 'blob' });
    return response.data;
  },

  generateRepaymentSchedule: async (id: number) => {
    const response = await api.post(`/applications/${id}/generate-repayment-schedule`, {}, { responseType: 'blob' });
    return response.data;
  },

  generateOperatorApplication: async (id: number) => {
    const response = await api.post(`/applications/${id}/generate-operator-application`, {}, { responseType: 'blob' });
    return response.data;
  },

  loanCompletenessCheck: async (id: number) => {
    const response = await api.post(`/applications/${id}/loan-completeness-check`);
    return response.data;
  },

  generateLoanContract: async (id: number) => {
    const response = await api.post(`/applications/${id}/generate-loan-contract`, {}, { responseType: 'blob' });
    return response.data;
  },

  // ZUS-specific AI endpoints
  zusDebtAnalysis: async (id: number) => {
    const response = await api.post(`/applications/${id}/zus-debt-analysis`, {}, { responseType: 'blob' });
    return response.data;
  },

  zusNegotiationStrategy: async (id: number) => {
    const response = await api.post(`/applications/${id}/zus-negotiation-strategy`, {}, { responseType: 'blob' });
    return response.data;
  },

  zusApplicationLetter: async (id: number, solutionType?: string) => {
    const response = await api.post(`/applications/${id}/zus-application-letter`, { solutionType }, { responseType: 'blob' });
    return response.data;
  },

  zusCompletenessCheck: async (id: number) => {
    const response = await api.post(`/applications/${id}/zus-completeness-check`);
    return response.data;
  },

  checkDuplicates: async (data: { email?: string; phone?: string; nip?: string; excludeId?: number }) => {
    const response = await api.post('/applications/check-duplicates', data);
    return response.data;
  },

  getStatsByType: async () => {
    const response = await api.get('/stats-by-type');
    return response.data;
  },

  // AI Agent Management
  getAiAgentConfig: async () => {
    const response = await authAxios.get('/api/ai-agent/config');
    return response.data;
  },

  updateAiAgentConfig: async (config: Record<string, string>) => {
    const response = await authAxios.patch('/api/ai-agent/config', config);
    return response.data;
  },

  getAiConversations: async (params?: { channel?: string; limit?: number; offset?: number }) => {
    const response = await authAxios.get('/api/ai-agent/conversations', { params });
    return response.data;
  },

  getAiConversation: async (id: number) => {
    const response = await authAxios.get(`/api/ai-agent/conversations/${id}`);
    return response.data;
  },

  getAiStats: async () => {
    const response = await authAxios.get('/api/ai-agent/stats');
    return response.data;
  },

  testAiAgent: async (message: string) => {
    const response = await authAxios.post('/api/ai-agent/test', { message });
    return response.data;
  },

  // ─── Clients Database ───
  getClients: async (params?: { page?: number; limit?: number; search?: string; year?: number; tag?: string; sortBy?: string; sortDir?: string }) => {
    const response = await authAxios.get('/api/clients', { params });
    return response.data;
  },

  getClientStats: async () => {
    const response = await authAxios.get('/api/clients/stats');
    return response.data;
  },

  getClient: async (id: number) => {
    const response = await authAxios.get(`/api/clients/${id}`);
    return response.data;
  },

  createClient: async (data: Record<string, unknown>) => {
    const response = await authAxios.post('/api/clients', data);
    return response.data;
  },

  updateClient: async (id: number, data: Record<string, unknown>) => {
    const response = await authAxios.patch(`/api/clients/${id}`, data);
    return response.data;
  },

  deleteClient: async (id: number) => {
    const response = await authAxios.delete(`/api/clients/${id}`);
    return response.data;
  },

  updateClientTags: async (id: number, add?: string[], remove?: string[]) => {
    const response = await authAxios.post(`/api/clients/tags/${id}`, { add, remove });
    return response.data;
  },

  bulkTagClients: async (clientIds: number[], tag: string, action?: 'add' | 'remove') => {
    const response = await authAxios.post('/api/clients/bulk-tag', { clientIds, tag, action });
    return response.data;
  },

  moveClientToKanban: async (clientId: number, serviceType: string) => {
    const response = await authAxios.post('/api/clients/move-to-kanban', { clientId, serviceType });
    return response.data;
  },

  syncClientsFromInvoices: async () => {
    const response = await authAxios.post('/api/clients/sync-from-invoices');
    return response.data;
  },

  importClientsExcel: async (file: File, sheets?: string[], skipExisting?: boolean) => {
    const formData = new FormData();
    formData.append('file', file);
    if (sheets) formData.append('sheets', JSON.stringify(sheets));
    if (skipExisting !== undefined) formData.append('skipExisting', String(skipExisting));
    const response = await authAxios.post('/api/clients/import-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  exportClientsCsv: async (clientIds?: number[]) => {
    const response = await authAxios.post('/api/clients/export-csv', { clientIds }, { responseType: 'blob' });
    return response.data;
  },

  // Excel Import
  previewExcel: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await authAxios.post('/api/admin/import-excel/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  importExcel: async (file: File, sheetName: string, serviceType: string, skipExisting: boolean) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sheetName', sheetName);
    formData.append('serviceType', serviceType);
    formData.append('skipExisting', String(skipExisting));
    const response = await authAxios.post('/api/admin/import-excel/execute', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Change requests (operator endpoints)
  getChangeRequests: async (applicationId: number) => {
    const response = await api.get(`/applications/${applicationId}/change-requests`);
    return response.data;
  },
  approveChange: async (applicationId: number, logId: number) => {
    const response = await api.post(`/applications/${applicationId}/change-requests/${logId}/approve`);
    return response.data;
  },
  rejectChange: async (applicationId: number, logId: number) => {
    const response = await api.post(`/applications/${applicationId}/change-requests/${logId}/reject`);
    return response.data;
  },

  // OCR Results
  getOcrResults: async (applicationId: number) => {
    const response = await api.get(`/applications/${applicationId}/ocr-results`);
    return response.data;
  },
  applyOcrResult: async (applicationId: number, ocrResultId: number, fieldsToApply: Record<string, unknown>) => {
    const response = await api.post(`/applications/${applicationId}/ocr-apply`, { ocrResultId, fieldsToApply });
    return response.data;
  },

  triggerWeeklyDigest: async () => {
    const response = await authAxios.post('/api/dashboard/weekly-digest');
    return response.data;
  },

  // Drip Campaigns
  getDripCampaigns: async () => {
    const response = await authAxios.get('/api/admin/drip-campaigns');
    return response.data;
  },
  createDripCampaign: async (data: { name: string; product_code?: string; trigger_event: string }) => {
    const response = await authAxios.post('/api/admin/drip-campaigns', data);
    return response.data;
  },
  updateDripCampaign: async (id: number, data: { is_active?: boolean; name?: string }) => {
    const response = await authAxios.patch(`/api/admin/drip-campaigns/${id}`, data);
    return response.data;
  },
  getDripCampaignSteps: async (campaignId: number) => {
    const response = await authAxios.get(`/api/admin/drip-campaigns/${campaignId}/steps`);
    return response.data;
  },
  createDripCampaignStep: async (campaignId: number, data: { step_order?: number; delay_hours?: number; subject: string; body_template: string; channel?: string }) => {
    const response = await authAxios.post(`/api/admin/drip-campaigns/${campaignId}/steps`, data);
    return response.data;
  },
  deleteDripCampaignStep: async (campaignId: number, stepId: number) => {
    const response = await authAxios.delete(`/api/admin/drip-campaigns/${campaignId}/steps/${stepId}`);
    return response.data;
  },
  getDripCampaignEnrollments: async (campaignId: number) => {
    const response = await authAxios.get(`/api/admin/drip-campaigns/${campaignId}/enrollments`);
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════
// CRM Staff Operators (assignment, team management)
// ═══════════════════════════════════════════════════════════

export interface CrmOperator {
  id: number;
  name: string;
  username: string;
  role: string;
  initials: string;
  first_name?: string;
  last_name?: string;
  allowed_products: string[] | null;
}

export const operatorsApi = {
  async getOperators(): Promise<{ operators: CrmOperator[] }> {
    const response = await authAxios.get('/api/auth/operators');
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════
// Products & Tags & Stage History (GrantOS Multi-Product)
// ═══════════════════════════════════════════��═══════════════

export const productsApi = {
  async getProducts() {
    const response = await authAxios.get('/api/products');
    return response.data;
  },
  async getProductStages(code: string) {
    const response = await authAxios.get(`/api/products/${code}/stages`);
    return response.data;
  },
  async getScoringConfig(code: string) {
    const response = await authAxios.get(`/api/products/admin/${code}/scoring-config`);
    return response.data;
  },
  async updateScoringConfig(code: string, config: Record<string, unknown> | object) {
    const response = await authAxios.put(`/api/products/admin/${code}/scoring-config`, config);
    return response.data;
  },
};

export const tagsApi = {
  async getAllTags() {
    const response = await authAxios.get('/api/tags');
    return response.data;
  },
  async createTag(name: string, color?: string, icon?: string, description?: string) {
    const response = await authAxios.post('/api/tags', { name, color, icon, description });
    return response.data;
  },
  async deleteTag(tagId: number) {
    const response = await authAxios.delete(`/api/tags/${tagId}`);
    return response.data;
  },
  async getApplicationTags(applicationId: number) {
    const response = await authAxios.get(`/api/tags/application/${applicationId}`);
    return response.data;
  },
  async getApplicationTagsBatch(applicationIds: number[]): Promise<Record<number, any[]>> {
    if (applicationIds.length === 0) return {};
    const response = await authAxios.post('/api/tags/application/batch', { applicationIds });
    return response.data;
  },
  async addTag(applicationId: number, tagId: number) {
    const response = await authAxios.post(`/api/tags/application/${applicationId}`, { tagId });
    return response.data;
  },
  async removeTag(applicationId: number, tagId: number) {
    const response = await authAxios.delete(`/api/tags/application/${applicationId}/${tagId}`);
    return response.data;
  },
  async getStageHistory(applicationId: number) {
    const response = await authAxios.get(`/api/tags/application/${applicationId}/stage-history`);
    return response.data;
  },
  async bulkTag(applicationIds: number[], tagId: number, action: 'add' | 'remove' = 'add') {
    const response = await authAxios.post('/api/tags/bulk', { applicationIds, tagId, action });
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════
// Fakturownia — Invoicing Integration
// ═══════════════════════════════════════════════════════════

export const invoicesApi = {
  async getStatus() {
    const response = await authAxios.get('/api/invoices/status');
    return response.data as { configured: boolean };
  },
  async getAll(params?: { page?: number; per_page?: number; period?: string; kind?: string; date_from?: string; date_to?: string; department_id?: string }) {
    const response = await authAxios.get('/api/invoices', { params });
    return response.data;
  },
  async getById(id: number) {
    const response = await authAxios.get(`/api/invoices/${id}`);
    return response.data;
  },
  async getByApplication(applicationId: number) {
    const response = await authAxios.get(`/api/invoices/by-application/${applicationId}`);
    return response.data as { invoices: Record<string, unknown>[] };
  },
  async createFromApplication(applicationId: number, data: {
    positions: Array<{ name: string; quantity?: number; tax?: number; total_price_gross: number; unit?: string }>;
    kind?: string;
    payment_to?: string;
    description?: string;
  }) {
    const response = await authAxios.post(`/api/invoices/from-application/${applicationId}`, data);
    return response.data;
  },
  async create(data: {
    buyer_name: string;
    buyer_tax_no?: string;
    buyer_email?: string;
    buyer_street?: string;
    buyer_city?: string;
    buyer_post_code?: string;
    buyer_country?: string;
    positions: Array<{ name: string; quantity?: number; tax?: number | string; total_price_gross: number; unit?: string }>;
    kind?: string;
    payment_to?: string;
    description?: string;
    department_id?: number;
  }) {
    const response = await authAxios.post('/api/invoices', data);
    return response.data;
  },
  async getDepartments() {
    const response = await authAxios.get('/api/invoices/departments');
    return response.data as { departments: Array<{ id: number; shortcut: string; name: string; tax_no: string; bank_account: string; main: boolean }> };
  },
  async sendByEmail(id: number) {
    const response = await authAxios.post(`/api/invoices/${id}/send`);
    return response.data;
  },
  async delete(id: number) {
    const response = await authAxios.delete(`/api/invoices/${id}`);
    return response.data;
  },
  async syncClient(applicationId: number) {
    const response = await authAxios.post(`/api/invoices/sync-client/${applicationId}`);
    return response.data;
  },
  async getProducts() {
    const response = await authAxios.get('/api/invoices/fk/products');
    return response.data;
  },
  getPdfUrl(id: number) {
    const token = localStorage.getItem('grant_crm_token');
    return `/api/invoices/${id}/pdf?token=${token}`;
  },
};

export const backupsApi = {
  async getHistory(limit = 20, offset = 0) {
    const response = await authAxios.get(`/api/admin/backups?limit=${limit}&offset=${offset}`);
    return response.data;
  },
  async getLastBackup() {
    const response = await authAxios.get('/api/admin/backups/last');
    return response.data;
  },
  async runBackup() {
    const response = await authAxios.post('/api/admin/backups/run');
    return response.data;
  },
};

export const emailTemplatesAdminApi = {
  async getAll(productCode?: string) {
    const params = productCode ? `?product_code=${productCode}` : '';
    const response = await authAxios.get(`/api/admin/email-templates${params}`);
    return response.data;
  },
  async getById(id: string) {
    const response = await authAxios.get(`/api/admin/email-templates/${id}`);
    return response.data;
  },
  async create(data: { id: string; product_code?: string; category: string; name: string; subject: string; body: string; sort_order?: number }) {
    const response = await authAxios.post('/api/admin/email-templates', data);
    return response.data;
  },
  async update(id: string, data: { product_code?: string; category: string; name: string; subject: string; body: string; sort_order?: number }) {
    const response = await authAxios.put(`/api/admin/email-templates/${id}`, data);
    return response.data;
  },
  async remove(id: string) {
    const response = await authAxios.delete(`/api/admin/email-templates/${id}`);
    return response.data;
  },
  async preview(data: { subject: string; body: string; variables?: Record<string, string> }) {
    const response = await authAxios.post('/api/admin/email-templates/preview', data);
    return response.data;
  },
};

// Constants re-exported from './constants' (see top of file)

// Industry categories admin API
export const industryAdminApi = {
  getAll: async () => {
    const response = await authAxios.get('/api/admin/industry-categories');
    return response.data;
  },
  getById: async (industryId: string) => {
    const response = await authAxios.get(`/api/admin/industry-categories/${industryId}`);
    return response.data;
  },
  create: async (data: { industry_id: string; name: string; category: string; clauses?: string[]; template_priority?: Record<string, number>; sort_order?: number }) => {
    const response = await authAxios.post('/api/admin/industry-categories', data);
    return response.data;
  },
  update: async (industryId: string, data: Partial<{ name: string; category: string; clauses: string[]; template_priority: Record<string, number>; sort_order: number; is_active: boolean }>) => {
    const response = await authAxios.put(`/api/admin/industry-categories/${industryId}`, data);
    return response.data;
  },
  remove: async (industryId: string) => {
    const response = await authAxios.delete(`/api/admin/industry-categories/${industryId}`);
    return response.data;
  },
  addPkd: async (industryId: string, prefix: string) => {
    const response = await authAxios.post(`/api/admin/industry-categories/${industryId}/pkd`, { prefix });
    return response.data;
  },
  removePkd: async (id: number) => {
    const response = await authAxios.delete(`/api/admin/industry-categories/pkd/${id}`);
    return response.data;
  },
  addKeyword: async (industryId: string, pattern: string, priority?: number) => {
    const response = await authAxios.post(`/api/admin/industry-categories/${industryId}/keywords`, { pattern, priority });
    return response.data;
  },
  removeKeyword: async (id: number) => {
    const response = await authAxios.delete(`/api/admin/industry-categories/keywords/${id}`);
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════
// Global Dashboard & Cross-Product APIs
// ═══════════════════════════════════════════════════════════

export const globalApi = {
  getGlobalStats: async () => {
    const response = await authAxios.get('/api/dashboard/global-stats');
    return response.data;
  },
  getCommunicationTimeline: async (applicationId: number) => {
    const response = await authAxios.get(`/api/communications/timeline/${applicationId}`);
    return response.data;
  },
  getNextBestAction: async (applicationId: number) => {
    const response = await authAxios.post('/api/ai/next-action', { applicationId });
    return response.data;
  },
  getCronStatus: async () => {
    const response = await authAxios.get('/api/admin/cron-status');
    return response.data;
  },
  triggerCronJob: async (jobName: string) => {
    const response = await authAxios.post('/api/admin/diagnostics/action', { action: 'restart-cron', target: jobName });
    return response.data;
  },

  // ── Client Stats (by product tag) ──────────────────────────────────────
  getClientDashboardStats: async () => {
    const response = await authAxios.get('/api/dashboard/client-stats');
    return response.data;
  },

  // ── Financial Stats (Fakturownia) ────────────────────────────────────────
  getFinancialStats: async () => {
    const response = await authAxios.get('/api/dashboard/financial-stats');
    return response.data;
  },

  // ── External Products Stats ─────────────────────────────────────────────
  getExternalProductStats: async () => {
    const response = await authAxios.get('/api/dashboard/external-products');
    return response.data;
  },

  // ── Grant Stats ─────────────────────────────────────────────────────────
  getGrantStats: async () => {
    const response = await authAxios.get('/api/grant-calls/stats/overview');
    return response.data;
  },

  getPipelineStats: async () => {
    const response = await authAxios.get('/api/dashboard/pipeline-stats');
    return response.data;
  },

  triggerWeeklyDigest: async () => {
    const response = await authAxios.post('/api/dashboard/weekly-digest');
    return response.data;
  },

  // ── OCR Documents ─────────────────────────────────────────────────────────
  getOcrDocuments: async (params: Record<string, string | number>) => {
    const response = await authAxios.get('/api/ocr/results', { params });
    return response.data;
  },
  getOcrDocument: async (id: number) => {
    const response = await authAxios.get(`/api/ocr/results/${id}`);
    return response.data;
  },
  getOcrDocumentStats: async () => {
    const response = await authAxios.get('/api/ocr/stats');
    return response.data;
  },
  rematchOcrDocuments: async () => {
    const response = await authAxios.post('/api/ocr/rematch');
    return response.data;
  },
  rematchOcrDocument: async (id: number) => {
    const response = await authAxios.post(`/api/ocr/rematch/${id}`);
    return response.data;
  },
  getOcrSuggestions: async (applicationId: number) => {
    const response = await authAxios.get(`/api/ocr/suggestions/${applicationId}`);
    return response.data;
  },
  reprocessOcrDocuments: async (opts: { engine?: string; limit?: number; maxConfidence?: number } = {}) => {
    const response = await authAxios.post('/api/ocr/reprocess', opts);
    return response.data;
  },
  applyOcrSuggestions: async (applicationId: number, fields: Record<string, string>) => {
    const response = await authAxios.post(`/api/ocr/apply-suggestions/${applicationId}`, { fields });
    return response.data;
  },

  // ── Monitoring ──────────────────────────────────────────────────────
  getMonitoringOverview: async () => {
    const response = await authAxios.get('/api/admin/monitoring/overview');
    return response.data;
  },
  getMonitoringMetrics: async () => {
    const response = await authAxios.get('/api/admin/monitoring/metrics');
    return response.data;
  },
  getMonitoringServices: async () => {
    const response = await authAxios.get('/api/admin/monitoring/services');
    return response.data;
  },
  getMonitoringErrors: async () => {
    const response = await authAxios.get('/api/admin/monitoring/errors');
    return response.data;
  },
  triggerServiceCheck: async () => {
    const response = await authAxios.post('/api/admin/monitoring/check-now');
    return response.data;
  },
  getMonitoringStack: async () => {
    const response = await authAxios.get('/api/admin/monitoring/stack');
    return response.data;
  },
  getMonitoringAuditLog: async (category?: string) => {
    const params = category ? `?category=${category}` : '';
    const response = await authAxios.get(`/api/admin/monitoring/audit-log${params}`);
    return response.data;
  },
  addAuditLogEntry: async (entry: { category: string; action: string; details?: any; risk_level?: string }) => {
    const response = await authAxios.post('/api/admin/monitoring/audit-log', entry);
    return response.data;
  },

};

// Gmail Integration (separate export to avoid TS object literal limit)
export const gmailApi = {
  inbox: async (account: string, options?: { page?: string; q?: string; limit?: number }) => {
    const params = new URLSearchParams({ account });
    if (options?.page) params.set('page', options.page);
    if (options?.q) params.set('q', options.q);
    if (options?.limit) params.set('limit', String(options.limit));
    const response = await authAxios.get(`/api/google/gmail/inbox?${params}`);
    return response.data;
  },
  thread: async (threadId: string, account?: string) => {
    const params = account ? `?account=${encodeURIComponent(account)}` : '';
    const response = await authAxios.get(`/api/google/gmail/thread/${threadId}${params}`);
    return response.data;
  },
  correspondence: async (email: string, account?: string) => {
    const params = account ? `?account=${encodeURIComponent(account)}` : '';
    const response = await authAxios.get(`/api/google/gmail/correspondence/${encodeURIComponent(email)}${params}`);
    return response.data;
  },
  send: async (data: { account: string; to: string; subject: string; body: string; cc?: string; html?: boolean }) => {
    const response = await authAxios.post('/api/google/gmail/send', data);
    return response.data;
  },
  reply: async (data: { account: string; threadId: string; body: string; html?: boolean }) => {
    const response = await authAxios.post('/api/google/gmail/reply', data);
    return response.data;
  },
  markRead: async (threadId: string, account: string) => {
    const response = await authAxios.post(`/api/google/gmail/thread/${threadId}/read`, { account });
    return response.data;
  },
  unread: async (account: string) => {
    const response = await authAxios.get(`/api/google/gmail/unread?account=${encodeURIComponent(account)}`);
    return response.data;
  },
};

// CRM Communication (Comments, Tasks, Timeline)
export const commApi = {
  // Comments
  comments: async (appId: number) => {
    const response = await authAxios.get(`/api/crm/applications/${appId}/comments`);
    return response.data;
  },
  addComment: async (appId: number, data: { content: string; parent_id?: number; mentions?: any[] }) => {
    const response = await authAxios.post(`/api/crm/applications/${appId}/comments`, data);
    return response.data;
  },
  updateComment: async (commentId: number, data: { content?: string; is_pinned?: boolean }) => {
    const response = await authAxios.patch(`/api/crm/comments/${commentId}`, data);
    return response.data;
  },
  deleteComment: async (commentId: number) => {
    const response = await authAxios.delete(`/api/crm/comments/${commentId}`);
    return response.data;
  },

  // Tasks
  tasks: async (appId: number, status?: string) => {
    const params = status ? `?status=${status}` : '';
    const response = await authAxios.get(`/api/crm/applications/${appId}/tasks${params}`);
    return response.data;
  },
  addTask: async (appId: number, data: { title: string; description?: string; task_type?: string; priority?: string; due_date?: string; assigned_to?: number; assigned_name?: string }) => {
    const response = await authAxios.post(`/api/crm/applications/${appId}/tasks`, data);
    return response.data;
  },
  updateTask: async (taskId: number, data: Record<string, any>) => {
    const response = await authAxios.patch(`/api/crm/tasks/${taskId}`, data);
    return response.data;
  },
  deleteTask: async (taskId: number) => {
    const response = await authAxios.delete(`/api/crm/tasks/${taskId}`);
    return response.data;
  },

  // Timeline
  timeline: async (appId: number, type?: string) => {
    const params = type ? `?type=${type}` : '';
    const response = await authAxios.get(`/api/crm/applications/${appId}/timeline${params}`);
    return response.data;
  },
  fieldChanges: async (appId: number) => {
    const response = await authAxios.get(`/api/crm/applications/${appId}/changes`);
    return response.data;
  },
};

// ─── Mailing Campaigns API ────────────────────────────────────────────
export const mailingApi = {
  // Campaigns CRUD
  getCampaigns: async (params?: { status?: string; search?: string; limit?: number; offset?: number }) => {
    const response = await authAxios.get('/api/mailing/campaigns', { params });
    return response.data;
  },
  getCampaign: async (id: number) => {
    const response = await authAxios.get(`/api/mailing/campaigns/${id}`);
    return response.data;
  },
  createCampaign: async (data: Record<string, any>) => {
    const response = await authAxios.post('/api/mailing/campaigns', data);
    return response.data;
  },
  updateCampaign: async (id: number, data: Record<string, any>) => {
    const response = await authAxios.put(`/api/mailing/campaigns/${id}`, data);
    return response.data;
  },
  deleteCampaign: async (id: number) => {
    const response = await authAxios.delete(`/api/mailing/campaigns/${id}`);
    return response.data;
  },

  // Recipients & Sending
  resolveRecipients: async (id: number, segmentConfig: Record<string, any>) => {
    const response = await authAxios.post(`/api/mailing/campaigns/${id}/resolve-recipients`, segmentConfig);
    return response.data;
  },
  sendCampaign: async (id: number) => {
    const response = await authAxios.post(`/api/mailing/campaigns/${id}/send`);
    return response.data;
  },
  scheduleCampaign: async (id: number, scheduledAt: string) => {
    const response = await authAxios.post(`/api/mailing/campaigns/${id}/schedule`, { scheduled_at: scheduledAt });
    return response.data;
  },
  pauseCampaign: async (id: number) => {
    const response = await authAxios.post(`/api/mailing/campaigns/${id}/pause`);
    return response.data;
  },
  getRecipients: async (id: number, params?: { status?: string; limit?: number; offset?: number }) => {
    const response = await authAxios.get(`/api/mailing/campaigns/${id}/recipients`, { params });
    return response.data;
  },

  // Stats & Events
  getEvents: async (id: number, params?: { limit?: number; offset?: number }) => {
    const response = await authAxios.get(`/api/mailing/campaigns/${id}/events`, { params });
    return response.data;
  },
  getStats: async (id: number) => {
    const response = await authAxios.get(`/api/mailing/campaigns/${id}/stats`);
    return response.data;
  },
  getDashboard: async () => {
    const response = await authAxios.get('/api/mailing/dashboard');
    return response.data;
  },

  // Utilities
  preview: async (htmlBody: string, sampleData?: Record<string, any>) => {
    const response = await authAxios.post('/api/mailing/preview', { html_body: htmlBody, sample_data: sampleData });
    return response.data;
  },
  uploadCsv: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await authAxios.post('/api/mailing/upload-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  getSenders: async () => {
    const response = await authAxios.get('/api/mailing/senders');
    return response.data;
  },
  getTemplates: async (productCode?: string) => {
    const response = await authAxios.get('/api/mailing/templates', { params: productCode ? { product_code: productCode } : {} });
    return response.data;
  },
  getProducts: async () => {
    const response = await authAxios.get('/api/mailing/products');
    return response.data;
  },
  testSend: async (id: number, testEmail: string) => {
    const response = await authAxios.post(`/api/mailing/campaigns/${id}/test-send`, { test_email: testEmail });
    return response.data;
  },

  // CSV export — downloads file via auth
  exportRecipients: async (id: number, status?: string) => {
    const params: any = {};
    if (status) params.status = status;
    const response = await authAxios.get(`/api/mailing/campaigns/${id}/recipients/export`, {
      params,
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `campaign_${id}_recipients.csv`;
    const disposition = response.headers['content-disposition'];
    if (disposition) {
      const match = disposition.match(/filename="([^"]+)"/);
      if (match) a.download = match[1];
    }
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },

  // Template CRUD
  createTemplate: async (data: Record<string, any>) => {
    const response = await authAxios.post('/api/mailing/templates', data);
    return response.data;
  },
  updateTemplate: async (id: number, data: Record<string, any>) => {
    const response = await authAxios.put(`/api/mailing/templates/${id}`, data);
    return response.data;
  },
  deleteTemplate: async (id: number) => {
    const response = await authAxios.delete(`/api/mailing/templates/${id}`);
    return response.data;
  },
  getTemplatePreview: async (id: number) => {
    const response = await authAxios.get(`/api/mailing/templates/${id}/preview`);
    return response.data;
  },

  // Unsubscribes
  getUnsubscribes: async (params?: { search?: string; limit?: number; offset?: number }) => {
    const response = await authAxios.get('/api/mailing/unsubscribes', { params });
    return response.data;
  },
  addUnsubscribe: async (email: string, reason?: string) => {
    const response = await authAxios.post('/api/mailing/unsubscribes', { email, reason });
    return response.data;
  },
  removeUnsubscribe: async (email: string) => {
    const response = await authAxios.delete(`/api/mailing/unsubscribes/${encodeURIComponent(email)}`);
    return response.data;
  },

  // Link analytics
  getLinkStats: async (id: number) => {
    const response = await authAxios.get(`/api/mailing/campaigns/${id}/link-stats`);
    return response.data;
  },

  // A/B Testing
  createAbTest: async (id: number, data: { variant_subject?: string; variant_html_body?: string; split_pct?: number }) => {
    const response = await authAxios.post(`/api/mailing/campaigns/${id}/ab-test`, data);
    return response.data;
  },
  getAbResults: async (id: number) => {
    const response = await authAxios.get(`/api/mailing/campaigns/${id}/ab-results`);
    return response.data;
  },

  // Recipient timeline
  getRecipientTimeline: async (campaignId: number, recipientId: number) => {
    const response = await authAxios.get(`/api/mailing/campaigns/${campaignId}/recipients/${recipientId}/timeline`);
    return response.data;
  },

  // Domain stats
  getDomainStats: async (campaignId?: number) => {
    const response = await authAxios.get('/api/mailing/domain-stats', { params: campaignId ? { campaign_id: campaignId } : {} });
    return response.data;
  },

  // Dashboard timeline
  getDashboardTimeline: async () => {
    const response = await authAxios.get('/api/mailing/dashboard/timeline');
    return response.data;
  },

  // Bulk unsubscribe
  bulkUnsubscribe: async (emails: string[], reason?: string) => {
    const response = await authAxios.post('/api/mailing/unsubscribes/bulk', { emails, reason });
    return response.data;
  },

  // Campaign tags
  updateCampaignTags: async (id: number, tags: string[]) => {
    const response = await authAxios.put(`/api/mailing/campaigns/${id}/tags`, { tags });
    return response.data;
  },
  getAllTags: async () => {
    const response = await authAxios.get('/api/mailing/tags');
    return response.data;
  },

  // Automations
  getAutomations: async () => {
    const response = await authAxios.get('/api/mailing/automations');
    return response.data;
  },
  createAutomation: async (data: Record<string, any>) => {
    const response = await authAxios.post('/api/mailing/automations', data);
    return response.data;
  },
  updateAutomation: async (id: number, data: Record<string, any>) => {
    const response = await authAxios.put(`/api/mailing/automations/${id}`, data);
    return response.data;
  },
  deleteAutomation: async (id: number) => {
    const response = await authAxios.delete(`/api/mailing/automations/${id}`);
    return response.data;
  },
  getAutomationLogs: async (id: number) => {
    const response = await authAxios.get(`/api/mailing/automations/${id}/logs`);
    return response.data;
  },

  // Resume / Reschedule / Cancel
  resumeCampaign: async (id: number) => {
    const response = await authAxios.post(`/api/mailing/campaigns/${id}/resume`);
    return response.data;
  },
  rescheduleCampaign: async (id: number, scheduledAt: string) => {
    const response = await authAxios.post(`/api/mailing/campaigns/${id}/reschedule`, { scheduled_at: scheduledAt });
    return response.data;
  },
  cancelSchedule: async (id: number) => {
    const response = await authAxios.post(`/api/mailing/campaigns/${id}/cancel-schedule`);
    return response.data;
  },

  // Heatmap
  getHeatmap: async (id: number) => {
    const response = await authAxios.get(`/api/mailing/campaigns/${id}/heatmap`);
    return response.data;
  },

  // Image upload
  uploadImage: async (base64: string, filename: string, contentType: string) => {
    const response = await authAxios.post('/api/mailing/upload-image', { image_base64: base64, filename, content_type: contentType });
    return response.data;
  },
  getImages: async () => {
    const response = await authAxios.get('/api/mailing/images');
    return response.data;
  },

  // Duplicate & Compare
  duplicateCampaign: async (id: number) => {
    const response = await authAxios.post(`/api/mailing/campaigns/${id}/duplicate`);
    return response.data;
  },
  compareCampaigns: async (ids: number[]) => {
    const response = await authAxios.post('/api/mailing/campaigns/compare', { campaign_ids: ids });
    return response.data;
  },
  duplicateTemplate: async (id: number, targetProductCode?: string) => {
    const response = await authAxios.post(`/api/mailing/templates/${id}/duplicate`, { target_product_code: targetProductCode });
    return response.data;
  },
};
